import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  useSyncStatus,
  useSyncRuns,
  useSyncTables,
  useSyncTrigger,
} from "../hooks/useSync";
import type {
  SyncSourceType,
  SyncRunStatus,
  SyncTableStatus,
} from "@shared/types";

// ── Utility helpers ─────────────────────────────────────────────────────────

function relativeTime(isoString: string | null | undefined): string {
  if (!isoString) return "Never";
  const date = new Date(isoString);
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function formatDateTime(isoString: string | null | undefined): string {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return "In progress";
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function secondsSince(date: Date | null): number {
  if (!date) return 0;
  return Math.floor((Date.now() - date.getTime()) / 1000);
}

// ── Type badges ─────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<SyncSourceType, string> = {
  airtable:
    "bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30",
  podio:
    "bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/30",
  d1: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30",
  access:
    "bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/30",
  typeform:
    "bg-pink-500/15 text-pink-600 dark:text-pink-400 border border-pink-500/30",
  wordpress:
    "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30",
  m365: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border border-sky-500/30",
  quickbooks:
    "bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/30",
};

const TYPE_LABELS: Record<SyncSourceType, string> = {
  airtable: "Airtable",
  podio: "Podio",
  d1: "D1",
  access: "Access",
  typeform: "Typeform",
  wordpress: "WordPress",
  m365: "M365",
  quickbooks: "QuickBooks",
};

function TypeBadge({ type }: { type: SyncSourceType }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[type] ?? "bg-theme-overlay text-theme-secondary"}`}
    >
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ── Run status badge ────────────────────────────────────────────────────────

const RUN_STATUS_STYLES: Record<SyncRunStatus, string> = {
  completed:
    "bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30",
  failed:
    "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30",
  partial:
    "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  running:
    "bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30",
};

const RUN_STATUS_DOT: Record<SyncRunStatus, string> = {
  completed: "bg-green-500",
  failed: "bg-red-500",
  partial: "bg-amber-500",
  running: "bg-blue-500 pulse-dot",
};

const RUN_STATUS_LABELS: Record<SyncRunStatus, string> = {
  completed: "Completed",
  failed: "Failed",
  partial: "Partial",
  running: "Running",
};

function RunStatusBadge({ status }: { status: SyncRunStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${RUN_STATUS_STYLES[status]}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full shrink-0 ${RUN_STATUS_DOT[status]}`}
      />
      {RUN_STATUS_LABELS[status]}
    </span>
  );
}

// ── Table status badge ──────────────────────────────────────────────────────

const TABLE_STATUS_STYLES: Record<SyncTableStatus, string> = {
  synced:
    "bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30",
  failed:
    "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30",
  pending:
    "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border border-zinc-500/30",
};

function TableStatusBadge({ status }: { status: SyncTableStatus }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${TABLE_STATUS_STYLES[status]}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ── Skeleton loaders ────────────────────────────────────────────────────────

function CardSkeleton() {
  return (
    <div className="glass rounded-2xl p-5 space-y-3">
      <div className="flex justify-between items-start">
        <div className="skeleton h-5 w-40 rounded-md" />
        <div className="skeleton h-5 w-16 rounded-full" />
      </div>
      <div className="skeleton h-3 w-24 rounded-md" />
      <div className="skeleton h-4 w-32 rounded-md" />
      <div className="flex gap-4">
        <div className="skeleton h-3 w-20 rounded-md" />
        <div className="skeleton h-3 w-20 rounded-md" />
      </div>
      <div className="skeleton h-8 w-24 rounded-lg" />
    </div>
  );
}

// ── Source status icon in cards ─────────────────────────────────────────────

function StatusIcon({ status }: { status: SyncRunStatus | "never" }) {
  if (status === "running") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#60a5fa"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="animate-spin"
        style={{ animationDuration: "2s" }}
      >
        <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
    );
  }
  if (status === "completed") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#16a34a"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  if (status === "failed") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#dc2626"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    );
  }
  if (status === "partial") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#d97706"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    );
  }
  // never
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-theme-muted"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

// ── Source card ─────────────────────────────────────────────────────────────

interface SourceCardProps {
  entry: {
    source: {
      id: string;
      name: string;
      type: SyncSourceType;
      schedule: string;
      is_active: number;
    };
    lastRun: {
      status: SyncRunStatus;
      started_at: string;
      completed_at: string | null;
      tables_synced: number;
      records_synced: number;
    } | null;
  };
  isAdmin: boolean;
  onTrigger: (sourceId: string) => void;
  isTriggering: boolean;
  triggerError: string;
}

function SourceCard({
  entry,
  isAdmin,
  onTrigger,
  isTriggering,
  triggerError,
}: SourceCardProps) {
  const { source, lastRun } = entry;
  const statusKey: SyncRunStatus | "never" = lastRun?.status ?? "never";

  const statusText: Record<SyncRunStatus | "never", string> = {
    running: "Syncing",
    completed: "Completed",
    failed: "Failed",
    partial: "Partial sync",
    never: "Never synced",
  };

  const statusColor: Record<SyncRunStatus | "never", string> = {
    running: "text-blue-600 dark:text-blue-400",
    completed: "text-green-700 dark:text-green-400",
    failed: "text-red-700 dark:text-red-400",
    partial: "text-amber-700 dark:text-amber-400",
    never: "text-theme-muted",
  };

  return (
    <div className="glass rounded-2xl p-5 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-theme-primary truncate">
            {source.name}
          </p>
          <p className="text-xs text-theme-muted mt-0.5">{source.schedule}</p>
        </div>
        <TypeBadge type={source.type} />
      </div>

      {/* Status row */}
      <div className="flex items-center gap-2">
        <StatusIcon status={statusKey} />
        <span className={`text-sm font-medium ${statusColor[statusKey]}`}>
          {statusText[statusKey]}
        </span>
        <span className="text-xs text-theme-muted ml-auto">
          {relativeTime(lastRun?.started_at)}
        </span>
      </div>

      {/* Stats */}
      {lastRun && (
        <div className="flex gap-4 text-xs text-theme-secondary">
          <span>
            <span className="text-theme-primary font-medium">
              {lastRun.records_synced.toLocaleString()}
            </span>{" "}
            records
          </span>
          <span>
            <span className="text-theme-primary font-medium">
              {lastRun.tables_synced}
            </span>{" "}
            tables
          </span>
        </div>
      )}

      {/* Trigger button */}
      {isAdmin && (
        <div className="pt-1">
          <button
            onClick={() => onTrigger(source.id)}
            disabled={
              isTriggering || statusKey === "running" || !source.is_active
            }
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-theme-overlay border border-theme-subtle text-theme-secondary hover:text-theme-primary hover:border-dcvfd-accent hover:bg-dcvfd/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isTriggering ? (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-spin"
                  style={{ animationDuration: "1s" }}
                >
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
                Triggering…
              </>
            ) : (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Sync Now
              </>
            )}
          </button>
          {triggerError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              {triggerError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Run row (expandable) ────────────────────────────────────────────────────

interface RunRowProps {
  run: {
    id: number;
    source_id: string;
    status: SyncRunStatus;
    started_at: string;
    completed_at: string | null;
    tables_synced: number;
    records_synced: number;
    records_failed: number;
    error_message: string | null;
    details: string | null;
  };
  sourceName: string;
  isOdd: boolean;
}

function RunRow({ run, sourceName, isOdd }: RunRowProps) {
  const [expanded, setExpanded] = useState(false);
  const rowBase = isOdd ? "bg-theme-overlay/40" : "";

  return (
    <>
      <tr
        className={`border-b border-theme-subtle hover:bg-theme-overlay transition-colors cursor-pointer ${rowBase}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-3 text-sm text-theme-primary font-medium whitespace-nowrap">
          {sourceName}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <RunStatusBadge status={run.status} />
        </td>
        <td className="px-4 py-3 text-sm text-theme-secondary whitespace-nowrap">
          {formatDateTime(run.started_at)}
        </td>
        <td className="px-4 py-3 text-sm text-theme-secondary whitespace-nowrap">
          {formatDuration(run.started_at, run.completed_at)}
        </td>
        <td className="px-4 py-3 text-sm text-theme-secondary text-right whitespace-nowrap">
          {run.tables_synced}
        </td>
        <td className="px-4 py-3 text-sm text-theme-secondary text-right whitespace-nowrap">
          {run.records_synced.toLocaleString()}
        </td>
        <td className="px-4 py-3 text-sm whitespace-nowrap text-right">
          <span
            className={
              run.records_failed > 0
                ? "text-red-600 dark:text-red-400 font-medium"
                : "text-theme-muted"
            }
          >
            {run.records_failed > 0 ? run.records_failed.toLocaleString() : "—"}
          </span>
        </td>
        <td className="px-4 py-3 text-theme-muted">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-theme-overlay">
          <td colSpan={8} className="px-4 py-3">
            <div className="text-xs font-mono space-y-2">
              {run.error_message && (
                <div>
                  <span className="text-theme-muted">Error: </span>
                  <span className="text-red-600 dark:text-red-400">
                    {run.error_message}
                  </span>
                </div>
              )}
              {run.details && (
                <div>
                  <span className="text-theme-muted block mb-1">Details:</span>
                  <pre className="text-theme-secondary bg-theme-surface rounded-lg p-3 overflow-x-auto text-xs leading-relaxed border border-theme-subtle">
                    {JSON.stringify(JSON.parse(run.details), null, 2)}
                  </pre>
                </div>
              )}
              {!run.error_message && !run.details && (
                <span className="text-theme-muted">
                  No additional details recorded.
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function SyncDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
    lastUpdatedAt,
    refresh,
  } = useSyncStatus(30_000);
  const {
    runs,
    isLoading: runsLoading,
    error: runsError,
  } = useSyncRuns(undefined, 20);
  const {
    tables,
    isLoading: tablesLoading,
    error: tablesError,
  } = useSyncTables();
  const { trigger, triggering, triggerError } = useSyncTrigger();

  // Live "last updated X seconds ago" counter
  const [secondsAgo, setSecondsAgo] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setSecondsAgo(secondsSince(lastUpdatedAt)),
      1000,
    );
    return () => clearInterval(t);
  }, [lastUpdatedAt]);

  // Filter state for Table Status section
  const [tableFilter, setTableFilter] = useState<string>("all");

  // Build a source name lookup from status data
  const sourceNameMap = new Map<string, string>(
    statusData?.map((e) => [e.source.id, e.source.name]) ?? [],
  );

  // Filtered tables
  const filteredTables =
    tableFilter === "all"
      ? tables
      : tables.filter((t) => t.source_id === tableFilter);

  // Trigger wrapper that also refreshes status
  const handleTrigger = async (sourceId: string) => {
    const result = await trigger(sourceId);
    if (result) {
      setTimeout(() => refresh(), 500);
    }
  };

  // Admin gate
  if (!isAdmin) {
    return (
      <div className="p-6 md:p-8 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-theme-primary">
            Sync Management
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Monitor and control data synchronization across DCVFD sources.
          </p>
        </div>
        <div className="glass rounded-2xl p-8 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-theme-primary mb-1">
            Access restricted
          </h2>
          <p className="text-theme-secondary text-sm">
            This page is restricted to administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Sync Management
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Monitor and control data synchronization across DCVFD sources.
          </p>
        </div>

        {/* Last updated indicator */}
        <div className="flex items-center gap-2 text-xs text-theme-muted">
          {statusLoading && !statusData ? (
            <span>Loading…</span>
          ) : (
            <>
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${statusError ? "bg-status-red" : "bg-status-green"}`}
              />
              <span>
                {statusError
                  ? "Error fetching status"
                  : `Updated ${secondsAgo < 5 ? "just now" : `${secondsAgo}s ago`}`}
              </span>
              <button
                onClick={refresh}
                className="ml-1 p-1 rounded hover:text-theme-primary hover:bg-theme-overlay transition-colors"
                title="Refresh now"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                  <path d="M16 21h5v-5" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Section 1: Source Status Cards ─────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Sources
        </h2>

        {statusError && (
          <div className="glass rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-400 border border-red-500/20 mb-4">
            Failed to load sources: {statusError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {statusLoading && !statusData
            ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
            : statusData?.map((entry) => (
                <SourceCard
                  key={entry.source.id}
                  entry={entry}
                  isAdmin={isAdmin}
                  onTrigger={handleTrigger}
                  isTriggering={triggering[entry.source.id] ?? false}
                  triggerError={triggerError[entry.source.id] ?? ""}
                />
              ))}
        </div>

        {!statusLoading && !statusError && statusData?.length === 0 && (
          <div className="glass rounded-xl px-4 py-8 text-center text-theme-muted text-sm">
            No sync sources configured.
          </div>
        )}
      </section>

      {/* ── Section 2: Recent Sync Runs ─────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Recent Runs
        </h2>

        {runsError && (
          <div className="glass rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-400 border border-red-500/20 mb-4">
            Failed to load runs: {runsError}
          </div>
        )}

        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-left">
              <thead>
                <tr className="border-b border-theme-subtle">
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
                    Tables
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
                    Records
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
                    Failed
                  </th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody>
                {runsLoading && runs.length === 0 ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-theme-subtle">
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width:
                                j === 7
                                  ? "16px"
                                  : `${60 + ((i * 17 + j * 11) % 60)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : runs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-theme-muted text-sm"
                    >
                      No sync runs recorded yet.
                    </td>
                  </tr>
                ) : (
                  runs.map((run, idx) => (
                    <RunRow
                      key={run.id}
                      run={run}
                      sourceName={
                        sourceNameMap.get(run.source_id) ?? run.source_id
                      }
                      isOdd={idx % 2 !== 0}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Section 3: Table Status ─────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
            Table Status
          </h2>

          {/* Source filter */}
          <div className="flex items-center gap-2">
            <label
              htmlFor="table-source-filter"
              className="text-xs text-theme-muted"
            >
              Filter by source:
            </label>
            <select
              id="table-source-filter"
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              className="text-xs bg-theme-overlay border border-theme-subtle rounded-lg px-2 py-1.5 text-theme-secondary focus:outline-none focus:border-dcvfd-accent"
            >
              <option value="all">All sources</option>
              {statusData?.map((e) => (
                <option key={e.source.id} value={e.source.id}>
                  {e.source.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {tablesError && (
          <div className="glass rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-400 border border-red-500/20 mb-4">
            Failed to load table status: {tablesError}
          </div>
        )}

        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-theme-subtle">
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Table
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Last Synced
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
                    Records
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
                    Delta
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                {tablesLoading && tables.length === 0 ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className="border-b border-theme-subtle">
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width: `${50 + ((i * 13 + j * 17) % 80)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredTables.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-theme-muted text-sm"
                    >
                      {tables.length === 0
                        ? "No table tracking data available yet."
                        : "No tables match the selected filter."}
                    </td>
                  </tr>
                ) : (
                  filteredTables.map((t) => (
                    <tr
                      key={t.id}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-theme-secondary whitespace-nowrap">
                        {sourceNameMap.get(t.source_id) ?? t.source_id}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-theme-primary whitespace-nowrap">
                        {t.table_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-theme-secondary whitespace-nowrap">
                        {relativeTime(t.last_synced_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-theme-secondary text-right whitespace-nowrap">
                        {t.last_record_count.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                        <span
                          className={
                            t.last_delta_count > 0
                              ? "text-brand-adaptive font-medium"
                              : "text-theme-muted"
                          }
                        >
                          {t.last_delta_count > 0
                            ? `+${t.last_delta_count.toLocaleString()}`
                            : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <TableStatusBadge status={t.last_status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-theme-muted max-w-[220px]">
                        {t.error_message ? (
                          <span
                            className="text-red-600 dark:text-red-400 truncate block cursor-help"
                            title={t.error_message}
                          >
                            {t.error_message.length > 60
                              ? `${t.error_message.slice(0, 60)}…`
                              : t.error_message}
                          </span>
                        ) : (
                          <span className="text-theme-muted opacity-50">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
